const safeDecodeURIComponent = (value) => {
  try {
    return decodeURIComponent(value);
  } catch (error) {
    return value;
  }
};

export const normalizeContainerFilterInput = (input) => {
  const trimmed = `${input ?? ''}`.trim();
  if (!trimmed) return '';

  try {
    const url = new URL(trimmed);
    if (url.hostname === 'id.labox-apps.com') {
      const businessId = url.searchParams.get('b');
      if (businessId) {
        return safeDecodeURIComponent(businessId);
      }
      return '';
    }
  } catch (error) {
    // Not a valid URL, fall through to regex handling.
  }

  const hostMatch = trimmed.match(/id\.labox-apps\.com\/(.+)/);
  if (hostMatch) {
    return safeDecodeURIComponent(hostMatch[1]);
  }

  const queryMatch = trimmed.match(/id\.labox-apps\.com\/c\?[^#]*\bb=([^&]+)/);
  if (queryMatch) {
    return safeDecodeURIComponent(queryMatch[1]);
  }

  return trimmed;
};

export const getMatchString = (item, field) => {
  if (!field) return '';

  if (Array.isArray(field)) {
    return field.map((key) => item?.[key] ?? '').join(' / ');
  }

  return item?.[field] ?? '';
};

export const getContainerId = (item, ivtContainerIdMapping) => getMatchString(item, ivtContainerIdMapping);

export const getContainerDisplayInfo = (item, ivtContainerDisplayInfoMapping) => (
  getMatchString(item, ivtContainerDisplayInfoMapping)
);

export const buildContainerSearchOptions = (containersArray, mappingConfig, excludedContainerId) => {
  const containers = Array.isArray(containersArray) ? containersArray : [];
  const ivtContainerIdMapping = mappingConfig?.columns?.ivtContainerId?.mapping?.csvName;
  const ivtContainerDisplayInfoMapping = mappingConfig?.columns?.ivtContainerDisplayInfo?.mapping?.csvName;

  return containers
    .filter((container) => container?.id !== excludedContainerId)
    .map((container) => {
      const containerId = getContainerId(container, ivtContainerIdMapping);
      const displayInfo = getContainerDisplayInfo(container, ivtContainerDisplayInfoMapping);
      const description = displayInfo && displayInfo !== containerId ? displayInfo : '';

      return {
        key: container.id,
        label: containerId,
        description,
        container,
        searchText: normalizeSearchText(`${containerId} ${displayInfo}`),
      };
    });
};

export const getContainerUrlPathReference = (item, ivtContainerIdMapping) => {
  if (Array.isArray(ivtContainerIdMapping)) {
    return ivtContainerIdMapping.map((key) => item?.[key] ?? '').join('_');
  }

  return item?.[ivtContainerIdMapping] ?? '';
};

export const getEncodedContainerUrlPathReference = (item, ivtContainerIdMapping) => (
  encodeURIComponent(getContainerUrlPathReference(item, ivtContainerIdMapping))
);

export const getOperationContainerPath = (item, ivtContainerIdMapping) => (
  `/inventory/operation/${getEncodedContainerUrlPathReference(item, ivtContainerIdMapping)}`
);

const normalizeSearchText = (value) => normalizeContainerFilterInput(value).toLowerCase();

const matchesQuery = (candidate, query) => normalizeSearchText(candidate).includes(query);

const equalsQuery = (candidate, query) => normalizeSearchText(candidate) === query;

export const findContainerMatch = (containersArray, mappingConfig, query) => {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) {
    return { status: 'empty' };
  }

  const containers = Array.isArray(containersArray) ? containersArray : [];

  const ivtContainerIdMapping = mappingConfig?.columns?.ivtContainerId?.mapping?.csvName;
  const ivtContainerDisplayInfoMapping = mappingConfig?.columns?.ivtContainerDisplayInfo?.mapping?.csvName;

  const exactMatches = containers.filter((container) => {
    const containerId = getContainerId(container, ivtContainerIdMapping);
    const containerDisplayInfo = getContainerDisplayInfo(container, ivtContainerDisplayInfoMapping);

    return equalsQuery(containerId, normalizedQuery)
      || equalsQuery(containerDisplayInfo, normalizedQuery);
  });

  if (exactMatches.length === 1) {
    return { status: 'matched', container: exactMatches[0] };
  }

  if (exactMatches.length > 1) {
    return { status: 'multiple' };
  }

  const partialMatches = containers.filter((container) => {
    const containerId = getContainerId(container, ivtContainerIdMapping);
    const containerDisplayInfo = getContainerDisplayInfo(container, ivtContainerDisplayInfoMapping);

    return matchesQuery(containerId, normalizedQuery)
      || matchesQuery(containerDisplayInfo, normalizedQuery);
  });

  if (partialMatches.length === 1) {
    return { status: 'matched', container: partialMatches[0] };
  }

  if (partialMatches.length > 1) {
    return { status: 'multiple' };
  }

  return { status: 'not_found' };
};
